import { Component, OnInit, OnDestroy, ChangeDetectorRef, HostListener, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { MatAngularModule } from '../../modules/mat-angular.module';
import { BarLineComboChartComponent } from '../../charts/bar-line-combo-chart/bar-line-combo-chart.component';
import { AreaChartComponent } from '../../charts/area-chart/area-chart.component';
// import { CitationCardComponent, Citation } from '../../components/citation-card/citation-card.component';
import { GenericTemplateCommonService } from '../../components/template-ai-common/services/generic-template-common.service';

@Component({
  selector: 'app-generic-performance-tracking',
  standalone: true,
  imports: [
    CommonModule,
    MatAngularModule,
    NgxChartsModule,
    BarLineComboChartComponent,
    AreaChartComponent,
    // CitationCardComponent,
  ],
  templateUrl: './generic-performance-tracking.component.html',
  styleUrl: './generic-performance-tracking.component.scss'
})
export class GenericPerformanceTrackingComponent implements OnInit, OnDestroy {
  @ViewChild('chartContainer', { static: false }) chartContainer!: ElementRef;
  
  // Core data properties
  performanceTrackingResponse: any = null;
  data: any = null;
  projectData: any = null;
  
  // Citations and error handling
  citations: any[] = [];
  errorMessage: string | null = null;
  hasError: boolean = false;

  // Visibility and memory management
  isTabVisible: boolean = false;
  isChartRendered: boolean = false;
  shouldRenderChart: boolean = false;

  // Chart data for different visualizations
  performanceRadarData: any[] = [];
  trendLineData: any[] = [];
  improvementBarData: any[] = [];
  xAxisTickValues: any[] = [];  // For controlling which dates show on x-axis

  // Bar chart properties (for your brand performance)
  results: any[] = []; // Grouped format for legacy compatibility
  barChartResults: any[] = []; // Flat array format for bar-line-combo-chart
  brands: any[] = [];

  // Responsive view dimensions
  view: [number, number] = [600, 400];

  // Subscriptions
  private subscriptions: Subscription[] = [];

  constructor(
    private templateCommonService: GenericTemplateCommonService,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef
  ) {}

  ngOnInit(): void {
    // Subscribe to Performance Tracking response
    const analysisSub = this.templateCommonService.performanceTrackingResponse$.subscribe((response: any) => {
      if (response) {
        this.performanceTrackingResponse = response;
        this.data = response;
        this.processPerformanceTrackingData(response);
        this.cdr.detectChanges();
      }
    });

    // Subscribe to project data
    const projectSub = this.templateCommonService.projectData$.subscribe((project: any) => {
      if (project) {
        this.projectData = project;
        this.cdr.detectChanges();
      }
    });

    // Add subscriptions to array
    this.subscriptions.push(analysisSub, projectSub);

    // Load data when component initializes
    this.loadPerformanceTrackingData();

    // Check visibility after component initialization
    setTimeout(() => {
      this.checkTabVisibility();
    }, 100);

    const currentProject = this.templateCommonService.getCurrentProjectData();
    if (currentProject) {
      this.projectData = currentProject;
    }
  }

  ngOnDestroy(): void {
    console.log('🗑️ Performance Tracking: Destroying component and clearing memory...');
    
    // Clear chart data to free memory
    this.clearChartData();
    
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
    
    // Don't clear service-level cache - data should persist across tab switches
    // The service manages the data and tabs subscribe to observables
    
    // Clear all local data references
    this.performanceTrackingResponse = null;
    this.data = null;
    this.projectData = null;
    
    // Reset visibility flags
    this.isTabVisible = false;
    this.isChartRendered = false;
    this.shouldRenderChart = false;
    
    console.log('✅ Performance Tracking: Component destroyed and memory cleared');
  }

  /**
   * Clear chart data to reduce memory usage
   */
  private clearChartData(): void {
    this.performanceRadarData = [];
    this.trendLineData = [];
    this.improvementBarData = [];
    this.results = [];
    this.barChartResults = [];
    this.brands = [];
    this.isChartRendered = false;
    this.shouldRenderChart = false;
  }

  /**
   * Check if tab is visible using Intersection Observer
   */
  @HostListener('window:scroll', ['$event'])
  onScroll(): void {
    this.checkTabVisibility();
  }

  /**
   * Check tab visibility and manage chart rendering
   */
  private checkTabVisibility(): void {
    if (!this.elementRef?.nativeElement) return;
    
    const rect = this.elementRef.nativeElement.getBoundingClientRect();
    const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
    
    if (isVisible !== this.isTabVisible) {
      this.isTabVisible = isVisible;
      console.log(`🔍 Performance Tracking: Tab visibility changed to ${isVisible}`);
      
      if (isVisible) {
        this.onTabBecameVisible();
      } else {
        this.onTabBecameHidden();
      }
    }
  }

  /**
   * Handle tab becoming visible
   */
  private onTabBecameVisible(): void {
    console.log('👁️ Performance Tracking: Tab became visible, rendering chart...');
    this.shouldRenderChart = true;
    this.renderChartIfNeeded();
  }

  /**
   * Handle tab becoming hidden
   */
  private onTabBecameHidden(): void {
    console.log('🙈 Performance Tracking: Tab became hidden, clearing chart...');
    this.shouldRenderChart = false;
    this.clearChartData();
    this.cdr.detectChanges();
  }

  /**
   * Render chart only if tab is visible and data is available
   */
  private renderChartIfNeeded(): void {
    if (!this.shouldRenderChart || this.isChartRendered) return;
    
    if (this.data) {
      console.log('📊 Performance Tracking: Rendering chart with data...');
      this.processPerformanceTrackingData(this.data);
      this.isChartRendered = true;
      this.cdr.detectChanges();
    }
  }


  loadPerformanceTrackingData(): void {
    // Check if we already have a response (data might have been loaded before component initialized)
    const currentResponse = this.templateCommonService.getCurrentPerformanceTrackingResponse();
    if (currentResponse) {
      console.log('Performance Tracking: Found existing response, processing immediately');
      this.performanceTrackingResponse = currentResponse;
      this.data = currentResponse;
      this.processPerformanceTrackingData(currentResponse);
      this.cdr.detectChanges();
    } else {
      // No data yet - subscription in ngOnInit() will handle it when data arrives
      console.log('Performance Tracking: No data yet, waiting for subscription to receive data');
    }
  }

  onReload(): void {
    this.templateCommonService.triggerTabReload('performance-tracking', true);
  }

  retryLoad(): void {
    this.onReload();
  }

  hasData(): boolean {
    return !!(this.data && (this.performanceRadarData.length > 0 || this.trendLineData.length > 0 || this.results.length > 0));
  }

  private processPerformanceTrackingData(data: any): void {
    try {
      console.log('🔍 Performance Tracking: Processing data:', data);
      
      // Reset error state
      this.hasError = false;
      this.errorMessage = null;
      this.citations = [];
      
      // Extract citations from response if available
      if (data && typeof data === 'object') {
        if (data.citations && Array.isArray(data.citations)) {
          this.citations = data.citations;
        } else if (data.metadata && data.metadata.citation_count > 0 && data.citations) {
          this.citations = Array.isArray(data.citations) ? data.citations : [];
        }
      }
      
      // Check if data is an error message
      if (data && typeof data === 'string' && data.includes('sorry')) {
        console.error('❌ Performance Tracking: Received error message instead of data:', data);
        this.hasError = true;
        this.errorMessage = data;
        this.data = null;
        this.performanceRadarData = [];
        this.trendLineData = [];
        this.improvementBarData = [];
        this.results = [];
        this.barChartResults = [];
        this.brands = [];
        this.cdr.markForCheck();
        return;
      }
      
      // Check for error in metadata (response field removed from backend)
      if (data.metadata && data.metadata.llm_error) {
        console.error('❌ Performance Tracking: LLM returned error:', data.metadata.error_message);
        this.hasError = true;
        this.errorMessage = data.metadata.error_message || 'LLM returned an error';
        // Extract citations from the data object even if there's an error
        if (data.citations && Array.isArray(data.citations)) {
          this.citations = data.citations;
        }
        this.data = null;
        this.performanceRadarData = [];
        this.trendLineData = [];
        this.improvementBarData = [];
        this.results = [];
        this.barChartResults = [];
        this.brands = [];
        this.cdr.markForCheck();
        return;
      }
      
      // Handle nested structured_data (response field removed from backend)
      let responseData = data;
      if (data.structured_data) {
        responseData = data.structured_data;
        console.log('✅ Performance Tracking: Using structured_data');
      }
      // Legacy support: if response field exists (old cached data), try to parse it
      else if (data.response && typeof data.response === 'string' && data.response.trim().startsWith('{')) {
        try {
          responseData = JSON.parse(data.response);
          console.log('✅ Performance Tracking: Parsed JSON response (legacy format)');
        } catch (e) {
          console.error('❌ Performance Tracking: Failed to parse response JSON:', e);
          this.data = null;
          this.performanceRadarData = [];
          this.trendLineData = [];
          this.improvementBarData = [];
          this.results = [];
          this.brands = [];
          this.cdr.markForCheck();
          return;
        }
      }

      // Validate that we have valid data structure
      if (!responseData || (typeof responseData === 'object' && Object.keys(responseData).length === 0)) {
        console.error('❌ Performance Tracking: Invalid or empty response data');
        this.hasError = true;
        this.errorMessage = 'Invalid or empty response data';
        // Extract citations from original data if available
        if (data && data.citations && Array.isArray(data.citations)) {
          this.citations = data.citations;
        }
        this.data = null;
        this.performanceRadarData = [];
        this.trendLineData = [];
        this.improvementBarData = [];
        this.results = [];
        this.barChartResults = [];
        this.brands = [];
        this.cdr.markForCheck();
        return;
      }
      
      // Extract citations from structured data if available
      if (responseData.context && responseData.context.citations) {
        this.citations = responseData.context.citations;
      } else if (responseData.citations && Array.isArray(responseData.citations)) {
        this.citations = responseData.citations;
      }

      console.log('✅ Performance Tracking: Valid data structure:', {
        hasBrands: !!responseData.brands,
        brandsCount: responseData.brands?.length || 0,
        hasTimeSeries: !!responseData.timeSeriesData,
        hasPlatformBreakdown: !!responseData.platformBreakdown
      });

      this.data = responseData;

      // Check visibility and process chart data
      this.checkTabVisibility();
      
      // Only process chart data if tab is visible
      if (this.isTabVisible) {
        this.performanceRadarData = this.getPerformanceRadarData(responseData);
        this.trendLineData = this.getTimeSeriesChartData(responseData);
        this.improvementBarData = this.getPlatformBreakdownData(responseData);
        
        // Process bar chart data for your brand performance
        this.brands = responseData.brands || [];
        this.prepareBarChartData();
        
        console.log('✅ Performance Tracking: Chart data prepared:', {
          radarDataLength: this.performanceRadarData.length,
          trendDataLength: this.trendLineData.length,
          improvementDataLength: this.improvementBarData.length,
          resultsLength: this.results.length,
          brandsLength: this.brands.length
        });
      } else {
        console.log('🔍 Performance Tracking: Tab not visible, deferring chart preparation');
      }

      this.cdr.markForCheck();
    } catch (error) {
      console.error('❌ Performance Tracking: Error processing data:', error, data);
      this.hasError = true;
      this.errorMessage = error instanceof Error ? error.message : 'Unknown error processing data';
      // Try to extract citations even on error
      if (data && data.citations && Array.isArray(data.citations)) {
        this.citations = data.citations;
      }
      this.data = null;
      this.performanceRadarData = [];
      this.trendLineData = [];
      this.improvementBarData = [];
      this.results = [];
      this.barChartResults = [];
      this.brands = [];
      this.cdr.markForCheck();
    }
  }
  
  // Get citations for display
  getCitations(): any[] {
    return this.citations || [];
  }
  
  // Check if we have citations to show
  hasCitations(): boolean {
    return this.citations && this.citations.length > 0;
  }

  // Map citations to CitationCard format
  get mappedCitations(): any[] {
  // get mappedCitations(): Citation[] {
    return (this.citations || []).map((c: any, idx: number) => ({
      id: typeof c.id === 'number' ? c.id : idx + 1,
      title: c.title || c.url || 'Source',
      url: c.url || '',
      snippet: c.snippet || '',
      domain: c.domain || this.extractDomain(c.url),
      confidence: typeof c.confidence === 'number' ? c.confidence : 0.9,
      type: c.type,
    }));
  }

  private extractDomain(url?: string): string {
    if (!url) return 'source';
    try {
      return new URL(url).hostname;
    } catch {
      return url;
    }
  }

  // Helper functions to process the JSON data
  private getPerformanceRadarData(data: any): any[] {
    if (!data.brands) return [];
    
    // Only show your brand in radar chart
    const youBrand = this.findYouBrand(data.brands);
    if (!youBrand) {
      console.log('🔍 Performance Tracking Radar: No your brand found');
      return [];
    }
    
    console.log('🔍 Performance Tracking Radar: Your brand found:', youBrand);
    
    return [{
      name: youBrand.brand,
      series: [
        { name: "Google Search", value: youBrand.google_search_visibility },
        { name: "ChatGPT", value: youBrand.chatgpt_mentions },
        { name: "Social Media", value: youBrand.social_media_buzz },
        { name: "News Coverage", value: youBrand.news_coverage },
        { name: "AI Platforms", value: youBrand.ai_platform_presence }
      ]
    }];
  }

  private getTimeSeriesChartData(data: any): any[] {
    if (!data.timeSeriesData || !data.brands || data.brands.length === 0) {
      // If no time series data, return empty array
      return [];
    }
    
    // Get your brand name and key
    const youBrand = this.findYouBrand(data.brands);
    if (!youBrand) return [];
    
    const brandName = youBrand.brand;
    const brandKey = brandName.toLowerCase();
    
    // For daily data, we always show last 30 days - no need for complex filtering
    // The backend already provides last 30 days of data
    let filteredTimeSeriesData = data.timeSeriesData;
    
    // If we have more than 30 data points, take the last 30 (most recent)
    if (filteredTimeSeriesData.length > 30) {
      filteredTimeSeriesData = filteredTimeSeriesData.slice(-30);
    }
    
    // If filtered data is empty, use all available data
    if (filteredTimeSeriesData.length === 0 && data.timeSeriesData.length > 0) {
      filteredTimeSeriesData = data.timeSeriesData;
    }
    
    // Calculate which x-axis ticks to show (weekly intervals: 1st, 7th, 14th, 21st, 28th, last)
    this.xAxisTickValues = [];
    if (filteredTimeSeriesData.length > 0) {
      // Show first date
      this.xAxisTickValues.push(filteredTimeSeriesData[0].period);
      
      // Show every 7th date
      for (let i = 6; i < filteredTimeSeriesData.length; i += 7) {
        this.xAxisTickValues.push(filteredTimeSeriesData[i].period);
      }
      
      // Always show last date if not already included
      const lastPeriod = filteredTimeSeriesData[filteredTimeSeriesData.length - 1].period;
      if (!this.xAxisTickValues.includes(lastPeriod)) {
        this.xAxisTickValues.push(lastPeriod);
      }
    }
    
    // Only return your brand's time series data
    return [
      {
        name: brandName,
        series: filteredTimeSeriesData.map((item: any) => ({
          name: item.period,
          value: item[brandKey] || 75
        }))
      }
    ];
  }

  private getPlatformBreakdownData(data: any): any[] {
    if (!data.platformBreakdown || !data.brands || data.brands.length === 0) return [];
    
    // Only show your brand in platform breakdown
    const youBrand = this.findYouBrand(data.brands);
    if (!youBrand) return [];
    
    const brandName = youBrand.brand;
    const brandKey = brandName.toLowerCase();
    
    return data.platformBreakdown.map((platform: any) => ({
      name: platform.platform,
      series: [
        { name: brandName, value: platform[brandKey] || 75 }
      ]
    }));
  }

  /**
   * Prepare bar chart data for your brand performance only
   */
  private prepareBarChartData(): void {
    if (!this.brands || this.brands.length === 0) {
      this.results = [];
      this.barChartResults = [];
      console.log('🔍 Performance Tracking Chart: No brands data available');
      return;
    }

    const youBrand = this.findYouBrand(this.brands);

    console.log('🔍 Performance Tracking Chart: All brands:', this.brands);
    console.log('🔍 Performance Tracking Chart: You brand:', youBrand);
    console.log('🔍 Performance Tracking Chart: Metadata your_brand:', this.data?.metadata?.your_brand);

    // Get platform metrics for x-axis
    const platformMetrics = [
      'Google Search Visibility',
      'ChatGPT Mentions', 
      'Social Media Buzz',
      'News Coverage',
      'AI Platform Presence'
    ];

    // Prepare bar chart data (results) - YOUR brand metrics as bars
    if (youBrand) {
      // Format data as flat array for bar-line-combo-chart
      const seriesData = platformMetrics
        .map((metric: string, index: number) => {
          const key = this.getPerformanceMetricKey(metric);
          const value = youBrand[key] || 0;
          console.log(`🔍 Performance Tracking Chart: ${metric} (${key}): ${value}`);
          return {
            name: metric,
            value: value
          };
        })
        .filter(item => item.value > 0); // Hide bars with zero values
      
      // Set flat array format for bar-line-combo-chart
      this.barChartResults = seriesData;
      
      // Format as grouped data for backward compatibility
      this.results = [{
        name: youBrand.brand,
        series: seriesData
      }];
      
      console.log('🔍 Performance Tracking Chart: Bar chart data prepared:', this.barChartResults);
    } else {
      this.results = [];
      this.barChartResults = [];
      console.log('🔍 Performance Tracking Chart: No your brand found, bar chart empty');
    }
  }

  /**
   * Helper method to get performance metric key from label
   */
  private getPerformanceMetricKey(label: string): string {
    const keyMap: { [key: string]: string } = {
      'Google Search Visibility': 'google_search_visibility',
      'ChatGPT Mentions': 'chatgpt_mentions',
      'Social Media Buzz': 'social_media_buzz',
      'News Coverage': 'news_coverage',
      'AI Platform Presence': 'ai_platform_presence',
      'Brand Recognition': 'brand_recognition',
      'Market Share': 'market_share',
      'Digital Presence': 'digital_presence'
    };
    return keyMap[label] || 'google_search_visibility';
  }

  /**
   * Helper methods following AI Citations pattern
   */
  private findYouBrand(brands: any[]): any {
    if (!brands || brands.length === 0) return null;
    
    // First try to get your brand from metadata
    if (this.data?.metadata?.your_brand) {
      const yourBrandName = this.data.metadata.your_brand;
      const youBrand = brands.find(brand => 
        brand.brand?.toLowerCase() === yourBrandName.toLowerCase() ||
        brand.name?.toLowerCase() === yourBrandName.toLowerCase()
      );
      if (youBrand) return youBrand;
    }
    
    // Look for brand marked as "your brand" or first brand
    const youBrand = brands.find(brand => brand.is_your_brand === true);
    return youBrand || brands[0];
  }



  // Chart event handlers
  onSelect(event: any): void {
    // Chart selection handler
  }

  onActivate(event: any): void {
    // Chart activation handler
  }

  onDeactivate(event: any): void {
    // Chart deactivation handler
  }

  // Get first brand name for chart
  get firstBrandName(): string {
    const youBrand = this.findYouBrand(this.brands || []);
    return youBrand ? (youBrand.brand || youBrand.name) : 'Your Brand';
  }

  // Helper methods for template
  hasPerformanceData(): boolean {
    return this.performanceRadarData.length > 0;
  }

  hasTrendData(): boolean {
    return this.trendLineData.length > 0;
  }

  getBrandName(): string {
    if (this.data?.brands && this.data.brands.length > 0) {
      return this.data.brands[0].brand;
    }
    return 'Brand';
  }

  getAnalysisPeriod(): string {
    // For daily data, always show last 30 days
    const currentDate = new Date();
    const startDate = new Date();
    startDate.setDate(currentDate.getDate() - 29); // Last 30 days (including today)
    
    const startMonth = startDate.toLocaleDateString('en-US', { month: 'short' });
    const startDay = startDate.getDate();
    const startYear = startDate.getFullYear();
    
    const endMonth = currentDate.toLocaleDateString('en-US', { month: 'short' });
    const endDay = currentDate.getDate();
    const endYear = currentDate.getFullYear();
    
    // Format: "Nov 1, 2024 - Nov 30, 2024"
    return `${startMonth} ${startDay}, ${startYear} - ${endMonth} ${endDay}, ${endYear}`;
  }

}